import { useRef, useState } from "react";

const SUBMIT_ADDR = "https://readdy.ai/api/form/dahh7rjoh653ivfvnvdg";

const feedbackTypes = ["内容投稿", "纠错反馈", "合作洽谈", "其他建议"];

export default function ContactForm() {
  const formRef = useRef<HTMLFormElement>(null);
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      formRef.current?.reset();
      return;
    }

    setStatus("submitting");
    setFormError("");

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === "website_alt") return;
      const text = String(value).trim();
      if (!text) return;
      params.append(key, text);
    });

    try {
      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: {
        code?: string;
        message?: string;
        meta?: { message?: string; detail?: string };
      } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText ||
        "";
      const isSpam = serverMsg.toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK";

      if (!ok || isSpam) {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交没有成功，请稍后重试，或直接发送邮件到 contact@gaoxiaochigua.cn。",
        );
        return;
      }

      setStatus("success");
      setFormError("");
      formRef.current?.reset();
    } catch {
      setStatus("error");
      setFormError("网络好像不太稳定，请稍后重试，或直接发送邮件到 contact@gaoxiaochigua.cn。");
    }
  };

  return (
    <form
      ref={formRef}
      data-readdy-form
      id="contact-form"
      onSubmit={handleSubmit}
      className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-7"
      noValidate
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-800">称呼</span>
          <input
            type="text"
            name="name"
            required
            placeholder="怎么称呼你"
            className="h-11 rounded-md border border-background-300 bg-background-100 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-800">邮箱</span>
          <input
            type="email"
            name="email"
            required
            placeholder="方便我们回复你"
            className="h-11 rounded-md border border-background-300 bg-background-100 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-800">所在学校</span>
          <input
            type="text"
            name="school"
            placeholder="例如：江城大学"
            className="h-11 rounded-md border border-background-300 bg-background-100 px-3.5 text-sm text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
          />
        </label>

        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground-800">反馈类型</span>
          <select
            name="type"
            defaultValue={feedbackTypes[0]}
            className="h-11 cursor-pointer rounded-md border border-background-300 bg-background-100 px-3 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
          >
            {feedbackTypes.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
      </div>

      <label className="mt-4 flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground-800">具体内容</span>
        <textarea
          name="message"
          rows={5}
          maxLength={500}
          required
          placeholder="想投稿的话题、希望补充的信息或合作想法，都可以写在这里（最多 500 字）"
          className="resize-none rounded-md border border-background-300 bg-background-100 p-3.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-500 focus:border-primary-400 focus:outline-none"
        ></textarea>
      </label>

      <input
        type="text"
        name="website_alt"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-hp-field"
      />

      {status === "error" && formError && (
        <p role="alert" className="mt-4 flex items-start gap-2 rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-800">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-error-warning-line"></i>
          </span>
          {formError}
        </p>
      )}

      {status === "success" && (
        <p role="status" className="mt-4 flex items-start gap-2 rounded-md bg-accent-100 px-4 py-3 text-sm text-accent-900">
          <span className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center">
            <i className="ri-checkbox-circle-line"></i>
          </span>
          已收到你的信息，我们通常会在三个工作日内回复，谢谢你的参与。
        </p>
      )}

      <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-foreground-500">
          提交即表示你同意我们仅在处理本次反馈时使用你的联系方式。
        </p>
        <button
          type="submit"
          disabled={status === "submitting"}
          className="flex cursor-pointer items-center gap-2 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60 whitespace-nowrap"
        >
          <i className="ri-send-plane-line text-base"></i>
          {status === "submitting" ? "提交中…" : "提交反馈"}
        </button>
      </div>
    </form>
  );
}